<template>
    <div class="flex items-center">
        <avatar-list v-if="displayLikers && displayLikers.length" :size="props.small ? 16 : (props.large ? 32 : 24)"
					 :transition="'slide-list-right-transition'"
					 no-wrap
					 rtl
					 :models="displayLikers" :max="props.max" :total="likesCount" class="cursor-pointer md:mr-4" @show-all="$emit('show')" @click="$emit('show')"/>

        <tooltip bottom :text="hasLiked ? $t('label.liked') : $t('action.like')">
            <btn :large="!props.small && !props.large" :x-small="props.small" :x-large="props.large" :class="{'h-32': props.small}" text @click.prevent="toggleLike()" :disabled="readonly" :color="hasLiked ? (color ?? $config.public.colors.primary) : null">
                <Icon :small="!props.small && !props.large" :x-small="props.small" :large="props.large" force-size :icon="likeIcon" :class="{'animated tada': hasJustLiked}"/>
                <span>{{ displayLikesCount }}</span>
            </btn>
        </tooltip>
    </div>
</template>

<script setup lang="ts">
    import {mdiPartyPopper} from "@mdi/js";
	import {ref, watch, computed} from 'vue';
	import {useRuntimeConfig, useNuxtApp, useRoute, useRouter} from "#imports";
	import {NativeApp} from "../../services";

	const props = withDefaults(defineProps<{
		// The URL where to send the like requests
		action?: string, // Can be null when readonly

		small?: boolean,
		large?: boolean,

		// The amount of likes
		count?: number,

		// The max displayed likes
		max?: number,

		// Whether the authenticated user has liked
		liked?: boolean,

		// The like objects to display
		likes: any[],

		readonly?: boolean,

		color?: string,
	}>(), {
		count: 0,
		max: 5,
		likes: []
	});

	const emit = defineEmits(['update:liked', 'update:likes', 'update:count', 'show']);
	const likeIcon = mdiPartyPopper;
	const config = useRuntimeConfig();
	const {$auth, $spoferanUrl, $apiFetch} = useNuxtApp();
	const route = useRoute();
	const router = useRouter();

	const likers = ref(props.likes);
	watch(() => props.likes, (newValue) => {
		likers.value = newValue;
	});
	watch(likers, (newValue) => {
		emit('update:likes', newValue);
	});

	const hasLiked = ref(props.liked);
	const hasJustLiked = ref(false);
	watch(() => props.liked, (newValue) => {
		hasLiked.value = newValue;
	});
	watch(hasLiked, (newValue) => {
		emit('update:liked', newValue);
	});

	const likesCount = ref(props.count);
	watch(() => props.count, (newValue) => {
		likesCount.value = newValue;
	});
	watch(likesCount, (newValue) => {
		emit('update:count', newValue);
	});

	// Mutated likers data to additionally show the liked authenticated user when the likes are cached before the auth user liked
	const displayLikers = computed(() => {
		if (hasLiked.value && $auth.id && !likers.value.find(liker => liker.id === $auth.id)) {
			return [$auth.user, ...likers.value];
		}

		return likers.value;
	});

	// Mutated likes count to additionally consider the authenticated user's like when the likes are cached before the auth user liked
	const displayLikesCount = computed(() => {
		if (!likesCount.value && hasLiked.value) {
			return 1;
		}

		return likesCount.value;
	});

   function toggleLike() {
	   if (props.readonly) {
		   return;
	   }

	   // User needs to be logged in to like an entity, so check it
	   if (!$auth.user) {
		   if (config.public.isSpoferanDomain) {
			   router.push({path: $spoferanUrl('login'), query: {redirect: route.fullPath}})
		   } else {
			   window.location.href = $spoferanUrl('login') + '?redirect=' + window.location.href
		   }
		   return
	   }

	   // Optimistic UI
	   let method = 'post';
	   if (hasLiked.value) {
		   likers.value = likers.value.filter((liker) => {
			   return liker.id !== $auth.user.id
		   })
		   hasLiked.value = false
		   hasJustLiked.value = false
		   likesCount.value--
		   method = 'delete';

		   NativeApp.vibrate('light');
	   } else {
		   likers.value = [$auth.user, ...likers.value]
		   hasLiked.value = true
		   hasJustLiked.value = true
		   likesCount.value++

		   NativeApp.vibrate('medium');
	   }

	   $apiFetch(props.action, {
		   method: method,
	   });

   }
</script>